import { addProductsPlp } from "./cart";
import { initializeCarousel } from "./components/collection-carousel";
import { carouselSizeHandler } from "./components/tabs-collections";

/**
 * Adds event listeners to the add to cart buttons on the product listing page.
 *
 * @param {Element} [target=null] The specific element within which to set up the listeners.
 */
export const setupAddToCartListeners = (target = null) => {
  const selector = '.add_to_cart_plp:not(.button--sold-out)';
  const buttons = target ? target.querySelectorAll(selector) : document.querySelectorAll(selector);

  buttons.forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      handleAddToCartClick(button);
    });
  });
};

/**
 * Handles click events on add to cart buttons, including mobile specific logic.
 *
 * @param {Element} button The button that was clicked.
 */
const handleAddToCartClick = (button) => {
  const tabsCollection = document.querySelector('.tabs-collections');
  if (shouldHideMobile(button)) {
    button.classList.add('mobile-hidden');
    return;
  }
  addProductsPlp(event);
  closeSizeSelectorQuery(event);

  if (window.screen.width <= 992 && tabsCollection) {
    const backdrop  = document.querySelector('.product-block__backdrop');
    const targetContainer = button.closest('.tabs-collections__content').querySelector('#tabs-sizes-container');
    const finalContainer = document.getElementById(window.sizeContainer.getAttribute("id"));
    while (targetContainer.firstChild) {
      finalContainer.appendChild(targetContainer.firstChild);
    }
    backdrop.style.display = 'none';
    document.body.classList.remove('body-no-scroll');
    targetContainer.classList.remove('active');
    window.sizeContainer = null;
  }
};

/**
 * Determines if the mobile-specific logic should be applied to hide the button.
 *
 * @param {Element} button The button to check.
 * @return {boolean} True if the button should be hidden, false otherwise.
 */
const shouldHideMobile = (button) => {
  return window.screen.width <= 992 &&
        !button.closest('.sizes-container__forms') &&
        button.classList.contains('with-sizes') &&
        !button.classList.contains('sizes') &&
        !button.classList.contains('btn-no-sizes');
};

/**
 * Closes the size selector for a product.
 *
 * @param {Event} event The event object.
 */
const closeSizeSelectorQuery = (event) => {
  const sizesContainer = event.target.closest('.product-block__sizes-container');
  if (sizesContainer) {
    const backdrop = sizesContainer.previousElementSibling;
    if (backdrop && backdrop.classList.contains('product-block__backdrop')) {
      closeSizeSelector(sizesContainer, backdrop);
    }
  }
};

/**
 * Closes the size selector by hiding the container and backdrop, and removing a no-scroll class from the body.
 *
 * @param {Element} sizeContainer The container element of the sizes.
 * @param {Element} backdrop The backdrop element.
 */
const closeSizeSelector = (sizeContainer, backdrop) => {
  if (sizeContainer && backdrop) {
    sizeContainer.classList.remove('active');
    document.body.classList.remove('body-no-scroll');
    backdrop.style.display = 'none';
  }
};

/**
 * Handles the display of sizes variants based on screen width and provided target element.
 *
 * @param {Element} [target=null] Optional target element to limit where we apply our behavior.
 */
export const showSizesVariants = (target = null) => {
  const isWideScreen = window.screen.width >= 768;
  const baseSelector = isWideScreen ? '.image-cont .sizes-container' : 'product-block';
  if (isWideScreen) {
    const containers = target ? target.querySelectorAll(baseSelector) : document.querySelectorAll(baseSelector);
    setupDesktopSizeContainers(containers);
  } else {
    const swiperContainer = document.querySelectorAll('.tabs-collections');
    // Check if we have a swiper container
    if (swiperContainer && swiperContainer.length > 0) {

      carouselSizeHandler(swiperContainer);

    } else {

      const containers = target ? target: document.querySelectorAll('product-block');
      setupMobileProductBlocks(containers);
    }
  }
};

/**
 * Sets up the event listeners for size containers on desktop view.
 *
 * @param {NodeList} containers Node list of container elements to setup.
 */
const setupDesktopSizeContainers = (containers) => {
  containers.forEach(container => {
    const sizeContainerForm = container.querySelector('.sizes-container__forms');
    const button = container.querySelector('.add_to_cart_plp:not(.sizes):not(.button--sold-out)');

    setupDesktopClickHandler(button, sizeContainerForm);
    setupDesktopMouseLeaveHandler(container, sizeContainerForm, button);
  });
};

/**
 * Adds a click event listener to buttons in desktop view that adjusts display properties on interaction.
 *
 * @param {Element} button Button element that triggers the size display.
 * @param {Element} sizeContainerForm Form element containing size options.
 */
const setupDesktopClickHandler = (button, sizeContainerForm) => {
  if (button && sizeContainerForm) {
    button.addEventListener('click', event => {
      event.preventDefault();
      sizeContainerForm.style.display = 'flex';
      sizeContainerForm.style.flexWrap = 'wrap';
      sizeContainerForm.style.justifyContent = 'flex-start';
      sizeContainerForm.parentElement.classList.add('bottom-move-container');
      button.style.display = 'none';
    });
  }
};

/**
 * Adds a mouse leave event listener to containers in desktop view to hide size options when the mouse leaves.
 *
 * @param {Element} container The container that holds the size options.
 * @param {Element} sizeContainerForm The form that displays the size options.
 * @param {Element} button The button that can trigger the size display.
 */
const setupDesktopMouseLeaveHandler = (container, sizeContainerForm, button) => {
  container.addEventListener('mouseleave', () => {
    if (sizeContainerForm && button) {
      sizeContainerForm.style.display = 'none';
      sizeContainerForm.parentElement.classList.remove('bottom-move-container');
      button.style.display = 'flex';
    }
  });
};

/**
 * Adds a click event listener to buttons in mobile view that adjusts display properties and adds a no-scroll class.
 *
 * @param {Element} button Button element that triggers the size display.
 * @param {Element} sizeContainerForm Form element containing size options.
 * @param {Element} backdrop The backdrop element to display when the form is active.
 */
const setupMobileClickHandler = (button, sizeContainerForm, backdrop) => {
  if (button && sizeContainerForm && backdrop) {
    button.addEventListener('click', event => {
      event.preventDefault();
      sizeContainerForm.classList.add('active');
      document.body.classList.add('body-no-scroll');
      backdrop.style.display = 'block';
    });
  }
};

/**
 * Adds a click event listener to the backdrop in mobile view to close the size selector.
 *
 * @param {Element} backdrop The backdrop element.
 * @param {Element} sizeContainerForm The form containing size options that needs to be closed.
 */
const setupBackdropClickHandler = (backdrop, sizeContainerForm) => {
  if (backdrop && sizeContainerForm) {
    backdrop.addEventListener('click', () => {
      closeSizeSelector(sizeContainerForm, backdrop);
    });
  }
};

/**
 * Configures mobile-specific event handlers for a product wrapper.
 * This function sets up click handlers responsible for showing and hiding
 * the size selection form on mobile devices, and also configures the backdrop
 * click behavior to close the form.
 *
 * @param {Element} wrapper - The product wrapper DOM element which contains the size selection form,
 *                            the add to cart button, and the product backdrop element.
 */
const handleWrapperMobile = (wrapper) => {
  if (wrapper) {
    const sizeContainerForm = wrapper.querySelector('.product-block__sizes-container');
    const button = wrapper.querySelector('.image-cont .sizes-container .add_to_cart_plp:not(.sizes):not(.button--sold-out)');
    const productBlockBackdrop = wrapper.querySelector('.product-block__backdrop');
    setupMobileClickHandler(button, sizeContainerForm, productBlockBackdrop);
    setupBackdropClickHandler(productBlockBackdrop, sizeContainerForm);
  }
}

/**
 * Sets up the event listeners for product blocks on mobile view.
 *
 * @param {NodeList} wrappers Node list of product block elements to setup.
 */
const setupMobileProductBlocks = (wrappers) => {
  if(wrappers.length){
    wrappers.forEach(wrapper => {
      handleWrapperMobile(wrapper);
    });
  } else{
    const productBlocks = document.querySelectorAll('product-block');
    if (productBlocks.length) handleWrapperMobile(productBlocks);
  }
};

window.addEventListener("DOMContentLoaded", () => {
    initializeCarousel();
});
